// checkin.view.tsx
import React, { useState, useEffect } from "react";
import Sidebar from "../../components/Center Admin/layout/Sidebar";
import "../../center admin css/welcome.css";
import { CheckInSidebarItem } from "./CheckInPage";
import ImageUploadBox from "../../components/Center Admin/Reuse/ImageUploadBox";
import DesktopCamera from "../../components/Center Admin/Reuse/DesktopCamera";
import { ReactComponent as CameraIcon } from "../../assets/icons/camera.svg";
import api from "../../api/axiosConfig"; // ✅ axios instance
import "../../center admin css/centerAdmin.css";
import { useS3Uploader } from "../../hooks/useS3Uploader";

interface CheckInViewProps {
  items: CheckInSidebarItem[];
  isLoading: boolean;
  error: string | null;
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  userName: string;
  onEmrSelect: (emrId: string | null) => void;
  childData: any;
  parentData: any;
}

const calculateAge = (dob: string): number => {
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

const getPathColor = (pathId: string) => {
  switch (pathId) {
    case "A":
      return "#ffff00";

    case "B":
      return "#8fd16f";

    case "C":
      return "#f777006b";

    case "D":
      return "#00b3ff77";

    case "E":
      return "#ff00fb6c";

    default:
      return "#f5f5f5";
  }
};

const CheckInView: React.FC<CheckInViewProps> = ({
  items,
  isLoading,
  error,
  selectedDate,
  onDateChange,
  userName,
  onEmrSelect,
}) => {
  const [selectedChildKey, setSelectedChildKey] = useState<
    string | undefined
  >();
  const [handoverKits, setHandoverKits] = useState<string[]>([]);
  const [idNumber, setIdNumber] = useState("");
  const [assignedPath, setAssignedPath] = useState("A");
  const [showPathDropdown, setShowPathDropdown] = useState(false);

  const PATH_OPTIONS = ["A", "B", "C", "D", "E"];
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [capturedPreview, setCapturedPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [capturedPhotoUrl, setCapturedPhotoUrl] = useState<string | null>(null);
  const [parentIdFromBackend, setParentIdFromBackend] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [savingHandover, setSavingHandover] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [operationsBoard, setOperationsBoard] = useState<any>(null);
  const [selectedSlotIndex, setSelectedSlotIndex] = useState(0);
  const [occupiedPaths, setOccupiedPaths] = useState<string[]>([]);

  const [localChildData, setLocalChildData] = useState<{
    dob?: string;
    gender?: string;
  } | null>(null);

  const [localParentData, setLocalParentData] = useState<{
    name?: string;
  } | null>(null);

  const emrIdForUploader = items.find((item) => item.key === selectedChildKey)
    ?.fullData?.child?.childId;

  const { uploadImage } = useS3Uploader(emrIdForUploader);

  useEffect(() => {
    setCapturedPreview(null);
    setCapturedPhotoUrl(null);
    setHandoverKits([]);
  }, [selectedChildKey]);

  useEffect(() => {
    const selectedItem = items.find((item) => item.key === selectedChildKey);

    const emrId = selectedItem?.fullData?.child?.childId ?? "";
    onEmrSelect(emrId);
  }, [selectedChildKey, items, onEmrSelect]);

  const toggleKit = (kit: string) => {
    setHandoverKits((prev) =>
      prev.includes(kit) ? prev.filter((k) => k !== kit) : [...prev, kit],
    );
  };

  const selectedItem = items.find((item) => item.key === selectedChildKey);

  /* ------------------------------------------------------------------
   ✅ NEW ROUTE (FINAL, CORRECT)
   GET /children/emr/:emrId/details
------------------------------------------------------------------ */
  useEffect(() => {
    const fetchChildAndParent = async () => {
      if (!selectedItem) return;

      const token = sessionStorage.getItem("accessToken");
      if (!token) return;

      try {
        const emrId = selectedItem.fullData.child.childId;

        const { data } = await api.parentchildApi.get(
          `/children/emr/${encodeURIComponent(emrId)}/details`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (data?.data) {
          setLocalChildData({
            dob: data.data.childDob,
            gender: data.data.childGender,
          });

          setLocalParentData({
            name: data.data.parentName,
          });

          // ✅ Auto-fill Aadhaar / PAN from API
          // setIdNumber(data.data.parentPanOrAadhaarNumber || "");
          const existingParentId = data.data.parentPanOrAadhaarNumber;

          setIdNumber(existingParentId ? String(existingParentId) : "");
          setParentIdFromBackend(
            Boolean(existingParentId && String(existingParentId).trim()),
          );
        }
      } catch (err) {
        console.error("Failed to fetch child + parent details:", err);
        setLocalChildData(null);
        setLocalParentData(null);
        setIdNumber("");
        setParentIdFromBackend(false);
      }
    };

    fetchChildAndParent();
  }, [selectedItem]);

  const fetchOperationsBoard = async () => {
    console.log("🔥 fetchOperationsBoard called");
    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) return;

      const centerId = sessionStorage.getItem("centerId");
      const releasedSlotId = sessionStorage.getItem("releasedSlotId");

      const workflowDate = selectedDate.toISOString().split("T")[0];

      const response = await api.centerworkflowApi.get(
        "/operations/status-board",
        {
          params: {
            centerId,
            date: workflowDate,
            releasedSlotId,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      console.log("Status Board Response:", response.data);

      setOperationsBoard(response.data);
    } catch (error) {
      console.error("Failed to fetch operations board", error);
    }
  };

  // useEffect(() => {
  //   fetchOperationsBoard();
  // }, [selectedDate]);

  useEffect(() => {
    fetchOperationsBoard();

    const interval = setInterval(() => {
      fetchOperationsBoard();
    }, 5000);

    return () => clearInterval(interval);
  }, [selectedDate]);

  useEffect(() => {
    const handlePatientUpdated = () => {
      console.log("🔥 patientUpdated received");
      fetchOperationsBoard();
    };

    window.addEventListener("patientUpdated", handlePatientUpdated);

    return () => {
      window.removeEventListener("patientUpdated", handlePatientUpdated);
    };
  }, [selectedDate]);

  /* ------------------------------------------------------------------ */

  useEffect(() => {
    if (!selectedItem) {
      setOccupiedPaths([]);
      return;
    }

    const currentReleasedSlotId = selectedItem.fullData.releasedSlotId;

    if (!currentReleasedSlotId) {
      setOccupiedPaths([]);
      return;
    }

    const occupied = items
      .filter(
        (item) =>
          item.fullData.releasedSlotId === currentReleasedSlotId &&
          item.fullData.pathId,
      )
      .map((item) => item.fullData.pathId);

    setOccupiedPaths(
      occupied.filter((path, index) => occupied.indexOf(path) === index),
    );

    console.log("Occupied Paths for Slot", currentReleasedSlotId, occupied);
  }, [items, selectedItem]);

  useEffect(() => {
    const availablePath = ["A", "B", "C", "D", "E"].find(
      (path) => !occupiedPaths.includes(path),
    );

    if (availablePath && occupiedPaths.includes(assignedPath)) {
      setAssignedPath(availablePath);
    }
  }, [occupiedPaths, assignedPath]);

  const handleHandoverConfirm = async () => {
    if (!selectedItem) {
      alert("No child selected.");
      return;
    }

    const token = sessionStorage.getItem("accessToken");
    if (!token) {
      alert("Session expired. Please login again.");
      return;
    }

    if (handoverKits.length === 0) {
      alert("Please select at least one handover kit.");
      return;
    }

    // ✅ EXACT payload backend expects
    const payload = {
      emrId: selectedItem.fullData.child.childId,
      "Handover Kits": {
        "Urine Sample Kit": handoverKits.includes("Urine Sample Kit"),
        "Blood Sample Kit": handoverKits.includes("Blood Sample Kit"),
        "Saliva Sample Kit": handoverKits.includes("Saliva Sample Kit"),
      },
    };

    try {
      setSavingHandover(true);

      const res = await api.dataCollectionApi.post(
        "/api/forms/handover-kits",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (res.data?.success) {
        alert("✅ Handover kits saved successfully");
        setHandoverKits([]); // reset only after success
      } else {
        throw new Error(res.data?.message || "Save failed");
      }
    } catch (err: any) {
      console.error("Handover kit save failed:", err);
      alert(
        err?.response?.data?.message ||
          "Failed to save handover kits. Please try again.",
      );
    } finally {
      setSavingHandover(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedItem) {
      alert("Please select a child before submitting.");
      return;
    }

    const token = sessionStorage.getItem("accessToken");
    if (!token) {
      alert("Session expired. Please log in again.");
      return;
    }

    try {
      setSubmitting(true);

      // ✅ Match Postman exactly

      const payload = {
        pathId: assignedPath,
      };

      const response = await api.centerworkflowApi.post(
        `/checkin/${encodeURIComponent(selectedItem.fullData.bookingId)}/assign-path`,
        {
          pathId: assignedPath,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.status === 200 || response.status === 201) {
        // const { pathId, currentStation, sessionId } = response.data;
        const { sessionId, firstStation } = response.data;

        sessionStorage.setItem("workflowSessionId", sessionId);

        // ✅ Update child photo URL + Parent PAN/Aadhaar
        const childId = selectedItem.fullData.child.childId;

        const updateChildPayload = {
          photoUrl: capturedPhotoUrl || undefined,
          parentPanOrAadhaarNumber: idNumber.trim() || undefined,
        };

        await api.parentchildApi.put(
          `/children/${encodeURIComponent(childId)}`,
          updateChildPayload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        alert(
          `✅ Path Assigned!\n\nFirst Station: ${firstStation}\nSession: ${sessionId}`,
        );

        // ✅ Reset stale data to prevent wrong queue display
        setSelectedChildKey(undefined);
        setAssignedPath("A");
        setHandoverKits([]);
        setIdNumber("");
        setUploadedFiles([]);
        setCapturedPhotoUrl(null);
        setParentIdFromBackend(false);

        // ✅ Optional: quick sync with backend
        window.location.reload();
      } else {
        alert("Unexpected response from server. Please check logs.");
      }
    } catch (error) {
      console.error("Assign path failed:", error);
      alert("Failed to assign path. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {successMessage && (
        <div
          style={{
            position: "fixed",
            top: "90px",
            right: "20px",
            background: "#08a5a5",
            color: "#fff",
            padding: "12px 18px",
            borderRadius: "8px",
            zIndex: 9999,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            fontWeight: 500,
          }}
        >
          ✅ {successMessage}
        </div>
      )}

      <div className="welcome-page">
        <div className="checkin-layout">
          <Sidebar
            items={items}
            activeKey={selectedChildKey}
            onSelect={setSelectedChildKey}
            selectedDate={selectedDate}
            onDateChange={onDateChange}
            isLoading={isLoading}
            noItemsMessage={error || "No appointments found for this date."}
            userName={userName}
          />

          <main className="checkin-main">
            {selectedItem ? (
              <>
                <section className="card">
                  <div className="card__title">Child & Parent Information</div>
                  <div className="checkin-grid">
                    {/* LEFT COLUMN */}
                    <div className="checkin-left">
                      <div className="checkin-field">
                        EMR ID:{" "}
                        <strong>{selectedItem.fullData.child.childId}</strong>
                      </div>
                      <div className="checkin-field">
                        Name:{" "}
                        <strong>{selectedItem.fullData.child.name}</strong>
                      </div>

                      <div className="checkin-field">
                        Age:{" "}
                        <strong>
                          {localChildData?.dob
                            ? `${calculateAge(localChildData.dob)} Years`
                            : "-"}
                        </strong>
                      </div>

                      <div className="checkin-field">
                        Gender: <strong>{localChildData?.gender || "-"}</strong>
                      </div>

                      <div className="checkin-panel">
                        <div className="panel__title">Capture Photo</div>

                        <div
                          style={{
                            textAlign: "center",
                            padding: "32px 20px",
                            minHeight: "220px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            border: "2px dashed #08a5a5",
                            borderRadius: "12px",
                            background: "#f9ffff",
                            transition: "all 0.2s ease",
                          }}
                        >
                          {capturedPreview ? (
                            <img
                              src={capturedPreview}
                              alt="Captured"
                              style={{
                                width: "100%",
                                maxWidth: "220px",
                                height: "220px",
                                objectFit: "cover",
                                borderRadius: "12px",
                                marginBottom: "14px",
                                border: "1px solid #d9d9d9",
                              }}
                            />
                          ) : (
                            <>
                              <CameraIcon
                                style={{
                                  width: 40,
                                  height: 40,
                                  marginBottom: 10,
                                }}
                              />
                              <div
                                style={{ fontWeight: 600, marginBottom: 12 }}
                              >
                                Image Upload
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  gap: "12px",
                                }}
                              >
                                <button
                                  type="button"
                                  className="btn btn--teal"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setShowCamera(true);
                                  }}
                                >
                                  Capture
                                </button>
                              </div>
                            </>
                          )}
                        </div>

                        {showCamera && (
                          <DesktopCamera
                            onConfirm={async (file) => {
                              try {
                                if (!selectedItem) {
                                  alert("No child selected");
                                  return;
                                }

                                const token =
                                  sessionStorage.getItem("accessToken");
                                if (!token) {
                                  alert("Session expired");
                                  return;
                                }

                                // 1️⃣ Upload to S3
                                const uploaded = await uploadImage(
                                  file,
                                  "checkin/capture-photo",
                                );

                                if (!uploaded?.key) {
                                  throw new Error("Upload failed");
                                }

                                setCapturedPhotoUrl(uploaded.key);

                                // 2️⃣ Save metadata in Data Collection microservice
                                await api.dataCollectionApi.post(
                                  "api/s3/checkin/capture-photo",
                                  {
                                    emrId: selectedItem.fullData.child.childId,
                                    imageKey: uploaded.key,
                                  },
                                  {
                                    headers: {
                                      Authorization: `Bearer ${token}`,
                                      "Content-Type": "application/json",
                                    },
                                  },
                                );

                                // 3️⃣ Update child profile with the S3 photo key
                                await api.centerworkflowApi.patch(
                                  `/children/${encodeURIComponent(
                                    selectedItem.fullData.child.childId,
                                  )}/profile`,
                                  {
                                    profile: uploaded.key,
                                  },
                                  {
                                    headers: {
                                      Authorization: `Bearer ${token}`,
                                      "Content-Type": "application/json",
                                    },
                                  },
                                );

                                // alert("✅ Photo captured and saved successfully");
                                setSuccessMessage(
                                  "Photo captured and saved successfully",
                                );

                                setTimeout(() => {
                                  setSuccessMessage("");
                                }, 3000);

                                setUploadedFiles([file]);
                                setCapturedPreview(URL.createObjectURL(file));
                                setShowCamera(false);
                              } catch (err) {
                                console.error(
                                  "Capture photo save failed:",
                                  err,
                                );
                                alert("Failed to save captured photo");
                              }
                            }}
                            onClose={() => setShowCamera(false)}
                          />
                        )}
                      </div>

                      <div className="checkin-panel">
                        <div className="panel__title">Handover Kits:</div>
                        {[
                          // "Urine Sample Kit",
                          "Blood Sample Kit",
                          "Saliva Sample Kit",
                        ].map((kit) => (
                          <label key={kit} className="checkbox">
                            <input
                              type="checkbox"
                              checked={handoverKits.includes(kit)}
                              onChange={() => toggleKit(kit)}
                            />{" "}
                            {kit}
                          </label>
                        ))}
                        <div style={{ marginTop: 8 }}>
                          <button
                            className="btn"
                            onClick={handleHandoverConfirm}
                            disabled={savingHandover}
                          >
                            {savingHandover ? "Saving..." : "Confirm"}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* RIGHT COLUMN */}
                    <div className="checkin-right">
                      <div className="checkin-field">
                        Parent Name:{" "}
                        <strong>{localParentData?.name || "-"}</strong>
                      </div>

                      <div className="checkin-field">
                        ID Number:{" "}
                        <input
                          className="text-input"
                          placeholder="Aadhaar / PAN"
                          value={idNumber}
                          onChange={(e) => setIdNumber(e.target.value)}
                          readOnly={parentIdFromBackend}
                        />
                      </div>
                      <div className="checkin-field">
                        Assign Path:
                        <div className="path-dropdown">
                          <div
                            className="path-dropdown-selected"
                            onClick={() =>
                              setShowPathDropdown(!showPathDropdown)
                            }
                          >
                            <span>Path {assignedPath}</span>

                            <span>{showPathDropdown ? "▲" : "▼"}</span>
                          </div>

                          {showPathDropdown && (
                            <div className="path-dropdown-menu">
                              {PATH_OPTIONS.map((path) => (
                                <div
                                  key={path}
                                  className={`path-dropdown-item ${
                                    occupiedPaths.includes(path)
                                      ? "path-dropdown-item-disabled"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    if (occupiedPaths.includes(path)) return;

                                    setAssignedPath(path);
                                    setShowPathDropdown(false);
                                  }}
                                >
                                  {/* Path {path} */}
                                  <>
                                    <span>
                                      {occupiedPaths.includes(path) ? "✓ " : ""}
                                      Path {path}
                                    </span>

                                    {occupiedPaths.includes(path) && (
                                      <span className="path-assigned-tag">
                                        Assigned
                                      </span>
                                    )}
                                  </>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="panel__title">QR Code</div>

                      <div className="qr-print-area">
                        <div className="qr-box qr-box--lg">
                          <img
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${selectedItem.fullData.child.childId}`}
                            alt="QR"
                            width="240"
                            height="240"
                          />
                        </div>
                      </div>

                      <div className="checkin-actions">
                        <button
                          className="btn btn--primary"
                          onClick={() => window.print()}
                        >
                          🖨️ Print
                        </button>

                        <button
                          className="btn btn--primary"
                          onClick={handleSubmit}
                          disabled={submitting}
                        >
                          {submitting ? "Submitting..." : "Submit"}
                        </button>
                      </div>
                    </div>
                  </div>
                </section>

                {/* {operationsBoard && operationsBoard.slots?.length > 0 && (
                  <section className="card" style={{ marginTop: "20px" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "20px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "32px",
                          fontWeight: 700,
                        }}
                      >
                        Operations Status Board
                      </div>

                      <select
                        value={selectedSlotIndex}
                        onChange={(e) =>
                          setSelectedSlotIndex(Number(e.target.value))
                        }
                        className="text-input"
                        style={{ width: "220px" }}
                      >
                        {operationsBoard.slots.map(
                          (slot: any, index: number) => (
                            <option key={index} value={index}>
                              {slot.slotLabel}
                            </option>
                          ),
                        )}
                      </select>
                    </div>

                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                      }}
                    >
                      <thead>
                        <tr>
                          <th className="ops-header">Slot ID</th>

                          
                          {operationsBoard.slots[selectedSlotIndex].columns.map(
                            (column: any) => (
                              <th key={column.stationId} className="ops-header">
                                {column.label}
                              </th>
                            ),
                          )}
                        </tr>
                      </thead>

                      <tbody>
                        {operationsBoard.slots[selectedSlotIndex].rows.map(
                          (row: any) => (
                            <tr key={row.pathOrder}>
                              <td className="ops-time">{row.timeLabel}</td>

                              {operationsBoard.slots[
                                selectedSlotIndex
                              ].columns.map((column: any) => {
                                const station = row.stations[column.stationId];

                                return (
                                  <td
                                    key={column.stationId}
                                    style={{
                                      background: getPathColor(station?.pathId),
                                      textAlign: "center",
                                      border: "1px solid #000",
                                      padding: "12px",
                                    }}
                                  >
                                    <div>{station?.childName}</div>

                                    <div>Path {station?.pathId}</div>

                                    {(() => {
                                      const status = station?.statusText || "";
                                      const parts = status.split(" - ");

                                      return (
                                        <>
                                          <div>Status: {parts[0]}</div>
                                          {parts[1] && <div>- {parts[1]}</div>}
                                        </>
                                      );
                                    })()}
                                  </td>
                                );
                              })}
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  </section>
                )} */}
              </>
            ) : (
              !isLoading && (
                <section className="card">
                  <div className="card__title">No Child Selected</div>
                  <p>{error || "Please select a child from the list ."}</p>
                </section>
              )
            )}

            {operationsBoard && operationsBoard.slots?.length > 0 && (
              <section className="card" style={{ marginTop: "20px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "32px",
                      fontWeight: 700,
                    }}
                  >
                    Operations Status Board
                  </div>

                  <select
                    value={selectedSlotIndex}
                    onChange={(e) =>
                      setSelectedSlotIndex(Number(e.target.value))
                    }
                    className="text-input"
                    style={{ width: "220px" }}
                  >
                    {operationsBoard.slots.map((slot: any, index: number) => (
                      <option key={index} value={index}>
                        {slot.slotLabel}
                      </option>
                    ))}
                  </select>
                </div>

                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                  }}
                >
                  <thead>
                    <tr>
                      <th className="ops-header">Slot ID</th>

                      {operationsBoard.slots[selectedSlotIndex].columns.map(
                        (column: any) => (
                          <th key={column.stationId} className="ops-header">
                            {column.label}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {operationsBoard.slots[selectedSlotIndex].rows.map(
                      (row: any) => (
                        <tr key={row.pathOrder}>
                          <td className="ops-time">{row.timeLabel}</td>

                          {operationsBoard.slots[selectedSlotIndex].columns.map(
                            (column: any) => {
                              const station = row.stations[column.stationId];

                              return (
                                <td
                                  key={column.stationId}
                                  style={{
                                    background: getPathColor(station?.pathId),
                                    textAlign: "center",
                                    border: "1px solid #000",
                                    padding: "12px",
                                  }}
                                >
                                  <div>{station?.childName}</div>

                                  <div>Path {station?.pathId}</div>

                                  {(() => {
                                    const status = station?.statusText || "";
                                    const parts = status.split(" - ");

                                    return (
                                      <>
                                        <div>Status: {parts[0]}</div>
                                        {parts[1] && <div>- {parts[1]}</div>}
                                      </>
                                    );
                                  })()}
                                </td>
                              );
                            },
                          )}
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </section>
            )}
          </main>
        </div>
      </div>
    </>
  );
};

export default CheckInView;
